import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Home, Star, MessageCircle, Phone, Mail } from 'lucide-react';
import { FaParking, FaShieldAlt, FaBolt, FaFaucet, FaBed, FaCompass } from 'react-icons/fa';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';


function PropertyInfo() {
  const [propertyData, setPropertyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [generatedDescription, setGeneratedDescription] = useState('');
  const mainImageContainerRef = useRef(null);
  const thumbnailContainerRef = useRef(null);
  const modalImageContainerRef = useRef(null);
  const { id } = useParams();
  const imageBaseUrl = 'http://provitapi.shop/realstateapp_api/';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isToolsOpen, setIsToolsOpen] = useState(false);
 const navigate = useNavigate();

  const handleHomeClick = () => {
    navigate('/');
  };
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleTools = () => {
    setIsToolsOpen(!isToolsOpen);
  };

  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`https://bhagirathiestate.com/api/get_property_detail.php?id=${id}`);
        
        if (response.data.status === 'success') {
          const property = response.data.data;
          if (property.images && property.images.length > 0) {
            property.images = property.images.map(image => 
              image.startsWith('http') ? image : imageBaseUrl + image
            );
          }
          property.isNegotiable = property.isNegotiable === '1' ? 'Yes' : property.isNegotiable;
          setPropertyData(property);
          
          // Generate description once and keep it static
          setGeneratedDescription(generateDescriptions(property));
        } else {
          setError(response.data.message || 'Failed to fetch property data');
        }
      } catch (err) {
        setError('Error fetching property data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchPropertyData();
    } else {
      setError('No property ID provided');
      setLoading(false);
    }
  }, [id]);

  const calculateParking = (squareYards) => {
    const yards = parseFloat(squareYards);
    if (isNaN(yards)) return 'N/A';
    if (yards >= 200 && yards <= 300) return '2';
    if (yards > 300 && yards <= 500) return '3';
    if (yards > 500 && yards <= 1000) return `${Math.floor(Math.random() * 3) + 5}`;
    return 'N/A';
  };

  const getParkingFieldValue = (property) => {
    const stiltType = property.stilt_type && property.stilt_type !== '' ? property.stilt_type : null;
    const parking = property.parking && property.parking !== '' && property.parking !== 'N/A' ? property.parking : null;
    const calculatedParking = calculateParking(property.square_yards);

    if (stiltType && parking) return `Stilt ${parking} ${stiltType} Parking`;
    if (parking) return parking;
    if (stiltType) return calculatedParking === 'N/A' ? 'N/A' : `${calculatedParking} ${stiltType} Parking`;
    return calculatedParking === 'N/A' ? 'N/A' : `${calculatedParking} Parking Spaces`;
  };

  const generateDescriptions = (property) => {
    const priceText = property => `${property.price} ${property.type === 'Buy' ? 'Crore' : 'Lakh'}${property.isNegotiable ? ' (negotiable)' : ''}`;
    const descriptions = [
      `Welcome to your dream home in the vibrant ${property.area}! This exceptional ${property.bhk} BHK property, spanning ${property.square_yards} square yards, is a perfect blend of modern luxury and practical living. Built in ${property.constructionYear}, this ${property.facing}-facing home offers a spacious and well-planned layout, ideal for families seeking comfort and style. Priced at ${priceText(property)}, it provides excellent value in a prime location. The property, located on ${property.floor}, includes ${getParkingFieldValue(property)} and a ${property.lift === 1 ? 'lift facility' : 'ground floor access'}, ensuring convenience for all. ${property.area} is known for its superb connectivity, with easy access to schools, hospitals, and shopping hubs, making daily life effortless. The interiors are designed to maximize natural light and ventilation, creating a warm and inviting atmosphere. With modern amenities and high-quality finishes, this home is perfect for both lively gatherings and peaceful family moments.`,
      `Discover luxury living in this stunning ${property.bhk} BHK residence in the heart of ${property.area}. Spanning ${property.square_yards} square yards, this ${property.constructionYear}-built property offers a perfect mix of contemporary design and functionality. Priced at ${priceText(property)}, this ${property.facing}-facing home is a fantastic investment opportunity. Located on ${property.floor}, it comes with ${getParkingFieldValue(property)} and a ${property.lift === 1 ? 'lift facility' : 'ground floor access'}, catering to modern family needs. The thoughtfully designed interiors provide ample space and comfort, while the prime location ensures excellent connectivity to key amenities like schools, markets, and healthcare facilities. Whether you're looking for a serene retreat or a vibrant urban lifestyle, this property in ${property.area} delivers it all with unmatched elegance and convenience.`
    ];
    return descriptions[Math.floor(Math.random() * descriptions.length)];
  };

  const scrollMainImageToIndex = (index) => {
    if (mainImageContainerRef.current) {
      const container = mainImageContainerRef.current;
      const scrollPosition = index * container.clientWidth;
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  const scrollModalImageToIndex = (index) => {
    if (modalImageContainerRef.current) {
      const container = modalImageContainerRef.current;
      const scrollPosition = index * container.clientWidth;
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
    scrollMainImageToIndex(index);
  };

  const handleMainImageScroll = (e) => {
    const container = e.target;
    const index = Math.round(container.scrollLeft / container.clientWidth);
    setCurrentImageIndex(index);
  };

  const handleModalImageScroll = (e) => {
    const container = e.target;
    const index = Math.round(container.scrollLeft / container.clientWidth);
    setCurrentImageIndex(index);
  };

  const openModal = (index) => {
    setCurrentImageIndex(index);
    setIsModalOpen(true);
    setTimeout(() => scrollModalImageToIndex(index), 100);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleModalDotClick = (index) => {
    setCurrentImageIndex(index);
    scrollModalImageToIndex(index);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-400"></div>
          <p className="mt-4 text-xl">Loading property details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-900/20 backdrop-blur-lg p-8 rounded-2xl border border-red-500/30">
            <h2 className="text-3xl font-bold mb-4 text-white">Error</h2>
            <p className="text-red-200">{error}</p>
            <button 
              onClick={() => window.history.back()}
              className="mt-6 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
            >
              ← Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!propertyData) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gray-800/50 backdrop-blur-lg p-8 rounded-2xl border border-gray-700">
            <h2 className="text-3xl font-bold mb-4 text-white">Property Not Found</h2>
            <button 
              onClick={() => window.history.back()}
              className="mt-6 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
            >
              ← Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>

        <header className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 border-b border-slate-700 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo Section */}
          <div className="flex items-center space-x-3">
            <img src="https://bhagirathiestate.com/assets/images/logo.png" alt="Logo" className="h-10 mr-3 rounded transition-transform duration-300 hover:scale-105" />
            <div>
              <h1 className="text-white text-xl font-bold tracking-tight">
                Bhagirathi Estate
              </h1>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button
              onClick={handleHomeClick}
              className="text-white hover:text-blue-300 transition-colors duration-200 font-medium py-2 bg-transparent border-none cursor-pointer"
            >
              Home
            </button>
            <a 
              href="https://bhagirathiestate.com/About.php" 
              className="text-white hover:text-blue-300 transition-colors duration-200 font-medium"
            >
              About Us
            </a>
          
            <a 
              href="https://bhagirathiestate.com/blog.php" 
              className="text-white hover:text-blue-300 transition-colors duration-200 font-medium"
            >
              Blogs
            </a>
            
            {/* Tools Dropdown */}
            <div className="relative">
              <button
                onClick={toggleTools}
                className="flex items-center space-x-1 text-white hover:text-blue-300 transition-colors duration-200 font-medium"
              >
                <span>Tools</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isToolsOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isToolsOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-slate-800 rounded-lg shadow-xl border border-slate-700 z-50">
                  <div className="py-2">
                    <a 
                      href="https://bhagirathiestate.com/emi-calculator.php" 
                      className="block px-4 py-2 text-white hover:bg-slate-700 hover:text-blue-300 transition-colors duration-200"
                    >
                      EMI Calculator
                    </a>
                    <a 
                      href="https://bhagirathiestate.com/rates-trends.php" 
                      className="block px-4 py-2 text-white hover:bg-slate-700 hover:text-blue-300 transition-colors duration-200"
                    >
                      Rates & Trend
                    </a>
                    <a 
                      href="https://bhagirathiestate.com/area-convertor.php" 
                      className="block px-4 py-2 text-white hover:bg-slate-700 hover:text-blue-300 transition-colors duration-200"
                    >
                     Area Calculator
                    </a>
                    
                  </div>
                </div>
              )}
            </div>

            <a 
              href="https://bhagirathiestate.com/contact.php" 
              className="text-white hover:text-blue-300 transition-colors duration-200 font-medium"
            >
              Contact
            </a>
          </nav>

          {/* Contact Us Button */}
          <div className="hidden lg:block">
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 shadow-md">
              Contact Us
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 rounded-lg text-white hover:bg-slate-700 transition-colors duration-200"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-slate-700 py-4">
            <nav className="flex flex-col space-y-4">
              <button
                onClick={handleHomeClick}
                className="text-white hover:text-blue-300 transition-colors duration-200 font-medium py-2 bg-transparent border-none cursor-pointer text-left"
              >
                Home
              </button>
              <a 
                href="#about" 
                className="text-white hover:text-blue-300 transition-colors duration-200 font-medium py-2"
              >
                About Us
              </a>
              <a 
                href="#property" 
                className="text-white hover:text-blue-300 transition-colors duration-200 font-medium py-2"
              >
                Property
              </a>
              <a 
                href="#blogs" 
                className="text-white hover:text-blue-300 transition-colors duration-200 font-medium py-2"
              >
                Blogs
              </a>
              
              {/* Mobile Tools Section */}
              <div>
                <button
                  onClick={toggleTools}
                  className="flex items-center space-x-1 text-white hover:text-blue-300 transition-colors duration-200 font-medium py-2"
                >
                  <span>Tools</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isToolsOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isToolsOpen && (
                  <div className="ml-4 mt-2 space-y-2">
                    <a 
                      href="#calculator" 
                      className="block text-slate-300 hover:text-blue-300 transition-colors duration-200 py-1"
                    >
                      EMI Calculator
                    </a>
                    <a 
                      href="#valuation" 
                      className="block text-slate-300 hover:text-blue-300 transition-colors duration-200 py-1"
                    >
                      Property Valuation
                    </a>
                    <a 
                      href="#comparison" 
                      className="block text-slate-300 hover:text-blue-300 transition-colors duration-200 py-1"
                    >
                      Compare Properties
                    </a>
                    <a 
                      href="#legal" 
                      className="block text-slate-300 hover:text-blue-300 transition-colors duration-200 py-1"
                    >
                      Legal Check
                    </a>
                  </div>
                )}
              </div>

              <a 
                href="#contact" 
                className="text-white hover:text-blue-300 transition-colors duration-200 font-medium py-2"
              >
                Contact
              </a>
              
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 mt-4 text-center">
                Contact Us
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center text-blue-300 text-base md:text-lg">
            <MapPin className="w-5 h-5 mr-2" />
            {propertyData.area}
          </div>
          <div className="flex items-center mt-2">
            <span className="text-2xl md:text-3xl font-bold text-green-400">
              {propertyData.price} {propertyData.type === 'Buy' ? 'CR' : 'Lakh'}
            </span>
            {propertyData.isNegotiable === 'Yes' && (
              <span className="ml-3 px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm">
                Negotiable
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
              <div className="relative">
                {propertyData.images && propertyData.images.length > 0 ? (
                  <>
                    <div 
                      ref={mainImageContainerRef}
                      className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                      style={{ scrollSnapType: 'x mandatory' }}
                      onScroll={handleMainImageScroll}
                    >
                      {propertyData.images.map((image, index) => (
                        <div 
                          key={index}
                          className="flex-shrink-0 w-full aspect-video snap-center"
                        >
                          <img 
                            src={image} 
                            alt={`Property ${propertyData.id} - ${index + 1}`}
                            className="w-full h-full object-cover cursor-pointer"
                            onClick={() => openModal(index)}
                          />
                        </div>
                      ))}
                    </div>
                    {propertyData.images.length > 1 && (
                      <div className="p-4">
                        <div 
                          ref={thumbnailContainerRef}
                          className="flex space-x-3 overflow-x-auto scrollbar-hide"
                        >
                          {propertyData.images.map((image, index) => (
                            <button
                              key={index}
                              onClick={() => handleThumbnailClick(index)}
                              className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                                index === currentImageIndex ? 'border-blue-400' : 'border-transparent'
                              }`}
                            >
                              <img 
                                src={image} 
                                alt={`Thumbnail ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="aspect-video bg-gray-700 flex items-center justify-center">
                    <Home className="w-16 h-16 text-gray-500" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bhagirathi Profile Section - Desktop (beside images) */}
          <div className="hidden lg:block">
            <div className="bg-gray-800 rounded-2xl border border-gray-700 p-6">
              <div className="text-center mb-6">
                <img 
                  src="https://bhagirathiestate.com/assets/images/logo.png" 
                  alt="Bhagirathi Estate" 
                  className="h-16 w-auto mx-auto mb-4"
                />
                <h3 className="text-xl font-bold text-white">Bhagirathi Estate</h3>
                <p className="text-blue-300">Buy & Sell Properties</p>
                <p className="text-gray-300 text-sm mt-2">Contact us for the best deals in real estate</p>
              </div>
              <div className="space-y-3">
                <button className="w-full flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl transition-all transform hover:scale-105">
                  <Phone className="w-5 h-5" />
                  <span>Call Now</span>
                </button>
                <button className="w-full flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl transition-all transform hover:scale-105">
                  <Mail className="w-5 h-5" />
                  <span>Send Email</span>
                </button>
                <button className="w-full flex items-center justify-center space-x-2 bg-green-500 hover:bg-green-600 text-white py-3 rounded-xl transition-all transform hover:scale-105">
                  <MessageCircle className="w-5 h-5" />
                  <span>WhatsApp</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Full-Screen Image Modal with Carousel */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
            <div className="relative w-full h-full">
              <div 
                ref={modalImageContainerRef}
                className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide h-full"
                style={{ scrollSnapType: 'x mandatory' }}
                onScroll={handleModalImageScroll}
              >
                {propertyData.images.map((image, index) => (
                  <div 
                    key={index}
                    className="flex-shrink-0 w-full h-full flex items-center justify-center snap-center"
                  >
                    <img 
                      src={image} 
                      alt={`Property ${propertyData.id} - ${index + 1}`}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ))}
              </div>
              
              <button 
                onClick={closeModal}
                className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all z-10"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              {propertyData.images.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
                  {propertyData.images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => handleModalDotClick(index)}
                      className={`w-3 h-3 rounded-full transition-all ${
                        index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-gray-800 rounded-2xl border border-gray-700 p-6">
              <h2 className="text-xl md:text-2xl font-bold text-white mb-6 flex items-center">
                <Star className="w-5 h-5 md:w-6 md:h-6 mr-3 text-yellow-400" />
                Amenities
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { 
                    name: propertyData.stilt_details ? `${propertyData.stilt_details} ${propertyData.stilt_type} Parking` : getParkingFieldValue(propertyData), 
                    icon: <FaParking className="text-base md:text-lg mr-2 text-yellow-400" />, 
                    label: 'Parking'
                  },
                  { 
                    name: 'Yes', 
                    icon: <FaShieldAlt className="text-base md:text-lg mr-2 text-yellow-400" />, 
                    label: 'Security'
                  },
                  { 
                    name: '24 Hours', 
                    icon: <FaBolt className="text-base md:text-lg mr-2 text-yellow-400" />, 
                    label: 'Power Backup'
                  },
                  { 
                    name: '24 Hours', 
                    icon: <FaFaucet className="text-base md:text-lg mr-2 text-yellow-400" />, 
                    label: 'Water Supply'
                  },
                  {
                    name: `${propertyData.bhk} Bedrooms`,
                    icon: <FaBed className="text-base md:text-lg mr-2 text-yellow-400" />, 
                    label: 'BHK'
                  },
                  {
                    name: `${propertyData.facing}`,
                    icon: <FaCompass className="text-base md:text-lg mr-2 text-yellow-400" />, 
                    label: 'Vastu Compliant'
                  }
                ].map((amenity, index) => (
                  <div key={index} className="p-4 rounded-xl border bg-gray-700/50 border-gray-600 text-gray-200 hover:bg-gray-600/70 transition-colors">
                    <div className="flex items-center">
                      {amenity.icon}
                      <span className="text-xs md:text-sm font-medium">{amenity.label}: {amenity.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gray-800 rounded-2xl border border-gray-700 p-6">
              <h2 className="text-xl md:text-2xl font-bold text-white mb-6">Property Description</h2>
              <p className="text-gray-300 leading-relaxed text-sm md:text-base text-justify">
                {generatedDescription}
              </p>
            </div>
          </div>
        </div>

        {/* Bhagirathi Profile Section - Mobile (at bottom) */}
        <div className="lg:hidden mt-8">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 p-6">
            <div className="text-center mb-6">
              <img 
                src="https://bhagirathiestate.com/assets/images/logo.png" 
                alt="Bhagirathi Estate" 
                className="h-16 w-auto mx-auto mb-4"
              />
              <h3 className="text-xl font-bold text-white">Bhagirathi Estate</h3>
              <p className="text-blue-300">Buy & Sell Properties</p>
              <p className="text-gray-300 text-sm mt-2">Contact us for the best deals in real estate</p>
            </div>
            <div className="space-y-3">
             <button className="w-full flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl transition-all transform hover:scale-105" onClick={() => window.location.href='tel:+919958068005'}>
  <Phone className="w-5 h-5" />
  <span>Call Now</span>
</button>
<button className="w-full flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl transition-all transform hover:scale-105" onClick={() => window.location.href='mailto:bhagirathiestate@gmail.com'}>
  <Mail className="w-5 h-5" />
  <span>Send Email</span>
</button>
<button className="w-full flex items-center justify-center space-x-2 bg-green-500 hover:bg-green-600 text-white py-3 rounded-xl transition-all transform hover:scale-105" onClick={() => window.location.href='https://wa.me/+919958068005'}>
  <MessageCircle className="w-5 h-5" />
  <span>WhatsApp</span>
</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PropertyInfo;